import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Grid,
  MenuItem,
} from '@mui/material';
import { toast } from 'react-toastify';
import CustomSelect from '../../../v2/MUI_custom_component/Select/CustomSelect';
import {
  fetchSuppliers,
  setSelectedSupplier,
  setSelectedEntity,
} from './actions/reduxActions';

const Entity = ({
  onSupplierChange,
  onEntityChange,
  returnFullData = false,
  autoFetch = true,
  forceRefresh = false,
  supplierLabel = "Manufacturer*",
  entityLabel = "SAP Code*",
  showManufacturerSelect = false,
  showEntitySelect = true,
  gridSizes = { 
    supplier: { xs: 6, sm: 6, md: 3 }, 
    entity: { xs: 12, sm: 12, md: 3 } 
  },
  disabledEntity = false,
  disabled = false,
}) => {
  const dispatch = useDispatch();
  const {
    suppliersList,
    selectedSupplier,
    selectedEntity,
    selectedSupplierData,
    loading,
    error
  } = useSelector((state) => state.entityReducer);

  useEffect(() => {
    if (autoFetch && (suppliersList.length === 0 || forceRefresh) && !loading) {
      dispatch(fetchSuppliers()).catch((err) => {
        toast.error(err.message || 'Failed to fetch suppliers');
      });
    }
  }, [autoFetch, suppliersList.length, forceRefresh]);

  // Handle supplier selection
  const handleSupplierChange = (value) => {
    dispatch(setSelectedSupplier(value));
    
    if (onSupplierChange) {
      if (returnFullData) {
        const supplierData = suppliersList.find(s => s.id === value);
        onSupplierChange({
          value: value,
          label: supplierData?.name || '',
          data: supplierData
        });
      } else {
        onSupplierChange(value);
      }
    }
  };

  // Handle entity selection
  const handleEntityChange = (value) => {
    dispatch(setSelectedEntity(value));
    
    if (onEntityChange) {
      if (returnFullData) {
        const entityData = selectedSupplierData?.entity_info?.find(e => e.esi_id === value);
        onEntityChange({
          value: value,
          label: entityData?.external_system_code || '',
          data: entityData
        });
      } else {
        onEntityChange(value);
      }
    }
  };

  // Show error toast
  useEffect(() => {
    if (error) {
      toast.error(error);
    }
  }, [error]);

  // Determine if supplier select should be disabled
  const isSupplierDisabled = disabled || loading || suppliersList.length <= 1;

  // Get current supplier data and entity info
  const currentSupplierData = selectedSupplierData || suppliersList.find(s => s.id === selectedSupplier);
  const hasEntities = currentSupplierData?.entity_info?.length > 0;
  const isEntityEnabled = currentSupplierData?.entity_enabled !== false;
  const isEntityDisabled = disabled || !currentSupplierData || currentSupplierData?.entity_info?.length <= 1 || disabledEntity;

  return (
    <Grid container spacing={2} mb={2}>
      {/* Manufacturer/Supplier Select */}
      {showManufacturerSelect && (
        <Grid item {...gridSizes.supplier}>
          <CustomSelect
            id="select-manufacturer"
            label={supplierLabel}
            value={selectedSupplier}
            disabled={isSupplierDisabled}
            onChange={(e) => handleSupplierChange(e.target.value)}
            fullWidth
          >
            {suppliersList.map((supplier) => (
              <MenuItem key={supplier.id} value={supplier.id}>
                {supplier.name}
              </MenuItem>
            ))}
          </CustomSelect>
        </Grid>
      )}
      

      {/* SAP Code/Entity Select */}
      {showEntitySelect && hasEntities && isEntityEnabled && (
        <Grid item {...gridSizes.entity}>
          <CustomSelect
            id="select-sap-code"
            label={entityLabel}
            value={selectedEntity}
            disabled={isEntityDisabled}
            onChange={(e) => handleEntityChange(e.target.value)}
            fullWidth
          >
            {currentSupplierData.entity_info.map((entity) => (
              <MenuItem key={entity.esi_id} value={entity.esi_id}>
                {entity.external_system_code}
              </MenuItem>
            ))}
          </CustomSelect>
        </Grid>
      )}
    </Grid>
  );
};

export default Entity;